import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api } from '../lib/api'
import { keys } from '../lib/queries'
import { formatRelative } from '../lib/format'
import type { Role } from '../lib/types'
import { Avatar, Button, Field, Input, Section, Select, Spinner, cx, useToast } from '../components/ui'
import { EmailSettingsSection, SlackSettingsSection } from './DeliverySettingsSections'
import { TagsSection } from './TagsSection'

export function AdminPage() {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[760px] px-5 py-6">
        <h1 className="text-[17px] font-semibold tracking-tight">Administration</h1>
        <p className="mt-1 text-[13px] text-[var(--ink-2)]">
          Who can sign in, how notifications get out, and the tags everyone shares.
        </p>

        <HealthSection />
        <InviteSection />
        <UsersSection />
        <EmailSettingsSection />
        <SlackSettingsSection />
        <TagsSection />
      </div>
    </div>
  )
}

// ----------------------------------------------------------------- health

function HealthSection() {
  const status = useQuery({
    queryKey: keys.adminStatus(),
    queryFn: api.adminStatus,
    refetchInterval: 30_000,
  })

  if (!status.data) {
    return (
      <Section title="Delivery">
        <Spinner className="text-[var(--ink-3)]" />
      </Section>
    )
  }

  const s = status.data

  return (
    <Section title="Delivery" hint="Notifications queue first and send in the background.">
      <div className="grid gap-2 sm:grid-cols-3">
        <Stat label="Queued" value={s.outbox.pending} />
        <Stat label="Sent (24h)" value={s.outbox.sent24h} />
        <Stat
          label="Failed"
          value={s.outbox.failed}
          tone={s.outbox.failed > 0 ? 'danger' : undefined}
        />
      </div>

      <div className="mt-4 space-y-2 text-[12.5px]">
        <Health
          ok={s.email.configured}
          label="Email"
          detail={
            s.email.configured
              ? `${s.email.host} · ${s.email.security} · from ${s.email.from}`
              : 'Not set up yet — fill in the Email section below'
          }
        />
        <Health
          ok={s.slack.botConfigured}
          label="Slack bot"
          detail={
            s.slack.botConfigured
              ? s.slack.linkingConfigured
                ? 'Bot and account linking are both set up'
                : 'Bot works; add a client ID and secret below to let people link their accounts'
              : 'Not set up yet — add a bot token in the Slack section below'
          }
        />
        <Health
          ok
          label="Live sync"
          detail={`${s.liveConnections} browser${s.liveConnections === 1 ? '' : 's'} connected`}
        />
      </div>
    </Section>
  )
}

function Stat({
  label, value, tone,
}: {
  label: string
  value: number
  tone?: 'danger'
}) {
  return (
    <div className="rounded-lg border border-[var(--line)] px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wider text-[var(--ink-3)]">{label}</p>
      <p
        className={cx(
          'task-key mt-0.5 text-[20px] font-semibold',
          tone === 'danger' && value > 0 ? 'text-[var(--danger)]' : 'text-[var(--ink)]',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function Health({
  ok, label, detail, action,
}: {
  ok: boolean
  label: string
  detail: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-[var(--line)] px-3 py-2">
      <span
        aria-hidden
        className={cx('h-1.5 w-1.5 shrink-0 rounded-full', ok ? 'bg-[var(--good)]' : 'bg-[var(--ink-3)]')}
      />
      <span className="font-medium">{label}</span>
      <span className="min-w-0 truncate text-[var(--ink-3)]">{detail}</span>
      {action && <span className="ml-auto shrink-0">{action}</span>}
    </div>
  )
}

// ------------------------------------------------------------ invitations

function InviteSection() {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('member')
  const invitations = useQuery({ queryKey: keys.invitations(), queryFn: api.invitations })
  const queryClient = useQueryClient()
  const toast = useToast()

  const invite = useMutation({
    mutationFn: () => api.invite(email, name, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.invitations() })
      toast.show(`Invitation sent to ${email}`)
      setEmail('')
      setName('')
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.deleteInvitation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.invitations() }),
  })

  const error = invite.error instanceof ApiError ? invite.error : null

  return (
    <Section
      title="Invitations"
      hint="There is no public sign-up. People join by following an invite link, which expires after seven days."
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Field label="Email" htmlFor="inv-email" error={error?.fields?.email}>
            <Input
              id="inv-email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="anna@artsol.dev"
            />
          </Field>
        </div>
        <div className="min-w-[140px] flex-1">
          <Field label="Name" htmlFor="inv-name">
            <Input
              id="inv-name" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Anna Petrova"
            />
          </Field>
        </div>
        <div className="w-[120px]">
          <Field label="Role" htmlFor="inv-role" error={error?.fields?.role}>
            <Select id="inv-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
        </div>
        <Button
          variant="primary"
          onClick={() => invite.mutate()}
          loading={invite.isPending}
          disabled={!email.trim()}
        >
          Send invite
        </Button>
      </div>

      {error && Object.keys(error.fields ?? {}).length === 0 && (
        <p className="mt-2 text-[12.5px] text-[var(--danger)]">{error.message}</p>
      )}

      {invitations.data && invitations.data.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Waiting</p>
          {invitations.data.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-3 rounded-md border border-[var(--line)] px-2.5 py-1.5 text-[12.5px]"
            >
              <span>{inv.email}</span>
              {inv.role === 'admin' && (
                <span className="rounded border border-[var(--line)] px-1 text-[10.5px] text-[var(--ink-3)]">
                  admin
                </span>
              )}
              <span className="ml-auto text-[11px] text-[var(--ink-3)]">
                sent {formatRelative(inv.createdAt)}
              </span>
              <button
                onClick={() => revoke.mutate(inv.id)}
                className="text-[11px] text-[var(--ink-3)] hover:text-[var(--danger)]"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

// ------------------------------------------------------------------ users

function UsersSection() {
  const users = useQuery({ queryKey: keys.adminUsers(), queryFn: api.adminUsers })
  const queryClient = useQueryClient()
  const toast = useToast()

  function onError(e: unknown) {
    const message =
      e instanceof ApiError
        ? Object.values(e.fields ?? {})[0] ?? e.message
        : 'Something went wrong'
    toast.show(message, { tone: 'error' })
  }

  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => api.setUserRole(id, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.adminUsers() }),
    onError,
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.setUserActive(id, active),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: keys.adminUsers() })
      toast.show(user.isActive ? `${user.name} can sign in again` : `${user.name} deactivated`)
    },
    onError,
  })

  if (!users.data) {
    return (
      <Section title="People">
        <Spinner className="text-[var(--ink-3)]" />
      </Section>
    )
  }

  return (
    <Section
      title="People"
      hint="Deactivating somebody blocks sign-in and frees their open tasks. Their comments and history stay."
    >
      <div className="space-y-1.5">
        {users.data.map((u) => (
          <div
            key={u.id}
            className={cx(
              'flex items-center gap-3 rounded-md border border-[var(--line)] px-2.5 py-2 text-[13px]',
              !u.isActive && 'opacity-60',
            )}
          >
            <Avatar user={u} size={26} />
            <div className="min-w-0">
              <p className="truncate font-medium leading-tight">{u.name}</p>
              <p className="task-key truncate text-[11px] leading-tight text-[var(--ink-3)]">
                {u.email}
              </p>
            </div>

            {!u.isActive && (
              <span className="rounded border border-[var(--line)] px-1 text-[10.5px] text-[var(--ink-3)]">
                deactivated
              </span>
            )}

            <div className="ml-auto flex items-center gap-2">
              <Select
                value={u.role}
                onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as Role })}
                className="h-7 w-[96px] py-0 text-[12px]"
                aria-label={`Role for ${u.name}`}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </Select>
              <Button
                size="sm"
                variant={u.isActive ? 'ghost' : 'default'}
                onClick={() => setActive.mutate({ id: u.id, active: !u.isActive })}
              >
                {u.isActive ? 'Deactivate' : 'Reactivate'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  )
}
